import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useParams } from "react-router-dom";

// Resources
import { ReactComponent as LogoCyStack } from "#src/assets/images/logos/logo-cystack.svg";
import { ReactComponent as LogoLocker } from "#src/assets/images/logos/logo-locker.svg";
import { ReactComponent as LogoWhitehub } from "#src/assets/images/logos/logo-whitehub.svg";

// General
import { helperPathname, pathname } from "#src/config/pathname";

// API-related
import { type ISolutionFromApi, listSolutions } from "#src/services/solutions";

// Children
import {
  SolutionCard,
  type ISolution,
} from "#src/components/solutions/SolutionCard";
import { solutionByTabs, SolutionLink } from "#src/components/solutions/const";
import { useAppSelector } from "#src/store/hooks";
import { BreadcrumbV2 } from "#src/commonV2/Breadcrumb";
import { ESolutionKey, ESolutionTabKey } from "#src/components/solutions/enum";
import { SolutionSectionWrapper } from "#src/components/solutions/SolutionSectionWrapper";

const AllSolutionsIndexPage = () => {
  const { t } = useTranslation("solutions");

  const { workspaceId } = useParams<"workspaceId">();

  const currentWorkspace = useAppSelector(
    (state) => state.auth.currentWorkspace
  );

  const [activeTab, setActiveTab] = useState<string>(ESolutionTabKey.ALL);
  const [solutions, setSolutions] = useState<ISolutionFromApi[]>([]);
  const onChangeTab = (key: string) => {
    setActiveTab(key);
  };

  // --------------- COMPUTED ---------------

  const tabList = useMemo(() => {
    return [
      {
        key: ESolutionTabKey.ALL,
        label: t("tabs.all"),
      },
      {
        key: ESolutionTabKey.VULNSCAN,
        label: t("tabs.vulnscan"),
      },
      {
        key: ESolutionTabKey.SECURITY_OPERATION,
        label: t("tabs.managedSecurityServices"),
      },
      {
        key: ESolutionTabKey.DATA_SECURITY,
        label: t("tabs.dataProtection"),
      },
      {
        key: ESolutionTabKey.SECURITY_COMPLIANCE,
        label: t("tabs.securityCompliance"),
      },
    ];
  }, [t]);

  /** Hardcoded solution list. */
  const defaultSolutions: Omit<ISolution, "enabled">[] = useMemo(
    () =>
      [
        {
          key: ESolutionKey.SECRETS_MANAGER,
          logo: <LogoLocker className="h-8 w-8 min-w-[2rem]" />,
          title: t("solution.secretsManager.title"),
          description: t("solution.secretsManager.description"),
          url: SolutionLink.LOCKER_SECRET_MANAGER,
          publicUrl: SolutionLink.LOCKER_SECRET_ADS,
        },
        {
          key: ESolutionKey.PASSWORD_MANAGER,
          logo: <LogoLocker className="h-8 w-8 min-w-[2rem]" />,
          title: t("solution.passwordManager.title"),
          description: t("solution.passwordManager.description"),
          url: SolutionLink.LOCKER_PASSWORD_MANAGER,
        },
        {
          key: ESolutionKey.ENDPOINT,
          logo: <LogoCyStack className="h-8 w-8 min-w-[2rem]" />,
          title: t("solution.endpoint.title"),
          description: t("solution.endpoint.description"),
          url: workspaceId
            ? generatePath(pathname.ENDPOINT_OVERVIEW, { workspaceId })
            : generatePath(pathname.SOLUTIONS, { workspaceId }),
          publicUrl: SolutionLink.ENDPOINT,
          isExternal: false,
        },
        {
          key: ESolutionKey.DATA_LEAK,
          logo: <LogoCyStack className="h-8 w-8 min-w-[2rem]" />,
          title: t("solution.dataLeak.title"),
          description: t("solution.dataLeak.description"),
          url: workspaceId
            ? generatePath(helperPathname.DATA_LEAK, { workspaceId })
            : generatePath(pathname.SOLUTIONS, { workspaceId }),
          publicUrl: SolutionLink.DATA_LEAK_ADS,
          isExternal: false,
        },
        {
          key: ESolutionKey.VULN_SCAN,
          logo: <LogoCyStack className="h-8 w-8 min-w-[2rem]" />,
          title: t("solution.vulnScan.title"),
          description: t("solution.vulnScan.description"),
          url: SolutionLink.VULNSCAN,
          comingSoon: true,
        },
        {
          key: ESolutionKey.TRUST_CENTER,
          logo: <LogoCyStack className="h-8 w-8 min-w-[2rem]" />,
          title: t("solution.trustCenter.title"),
          description: t("solution.trustCenter.description"),
          url: workspaceId
            ? generatePath(helperPathname.TRUST_CENTER, {
                workspaceId,
              })
            : generatePath(pathname.SOLUTIONS, { workspaceId }),
          publicUrl: SolutionLink.TRUST_CENTER_ADS,
          isExternal: false,
          isCreate: true,
        },
        {
          key: ESolutionKey.PENETRATION_TESTING,
          logo: <LogoCyStack className="h-8 w-8 min-w-[2rem]" />,
          title: t("solution.penetrationTesting.title"),
          description: t("solution.penetrationTesting.description"),
          url: SolutionLink.PENETRATION_TESTING,
          isCreate: true,
        },
        {
          key: ESolutionKey.RED_TEAMING,
          logo: <LogoCyStack className="h-8 w-8 min-w-[2rem]" />,
          title: t("solution.redTeaming.title"),
          description: t("solution.redTeaming.description"),
          url: SolutionLink.RED_TEAM,
          isCreate: true,
        },
        {
          key: ESolutionKey.CLOUD_SECURITY_AUDIT,
          logo: <LogoCyStack className="h-8 w-8 min-w-[2rem]" />,
          title: t("solution.cloudSecurityAudit.title"),
          description: t("solution.cloudSecurityAudit.description"),
          url: SolutionLink.INFRASTRUCTURE_AUDIT,
          isCreate: true,
        },
        {
          key: ESolutionKey.SOURCE_CODE_AUDIT,
          logo: <LogoCyStack className="h-8 w-8 min-w-[2rem]" />,
          title: t("solution.sourceCodeAudit.title"),
          description: t("solution.sourceCodeAudit.description"),
          url: SolutionLink.SOURCE_CODE_AUDIT,
          isCreate: true,
        },
        {
          key: ESolutionKey.BLOCKCHAIN_PROTOCOL_AUDIT,
          logo: <LogoCyStack className="h-8 w-8 min-w-[2rem]" />,
          title: t("solution.blockchainProtocolAudit.title"),
          description: t("solution.blockchainProtocolAudit.description"),
          url: SolutionLink.BLOCKCHAIN_AUDIT,
          isCreate: true,
        },
        {
          key: ESolutionKey.WHITEHUB_BUG_BOUNTY,
          logo: <LogoWhitehub className="h-8 w-8 min-w-[2rem]" />,
          title: t("solution.whitehubBugBounty.title"),
          description: t("solution.whitehubBugBounty.description"),
          url: SolutionLink.WHITEHUB_BUG_BOUNTY,
          isCreate: true,
        },
        {
          key: ESolutionKey.SECURITY_COMPLIANCE,
          logo: <LogoCyStack className="h-8 w-8 min-w-[2rem]" />,
          title: t("solution.securityCompliance.title"),
          description: t("solution.securityCompliance.description"),
          url: SolutionLink.COMPLIANCE_AUDIT_SERVICE,
          isCreate: true,
        },
        {
          key: ESolutionKey.SECURITY_OPERATIONS_CENTER,
          logo: <LogoCyStack className="h-8 w-8 min-w-[2rem]" />,
          title: t("solution.securityOperationsCenter.title"),
          description: t("solution.securityOperationsCenter.description"),
          url: SolutionLink.SOC,
          isCreate: true,
        },
        {
          key: ESolutionKey.VULNERABILITY_MANAGEMENT,
          logo: <LogoCyStack className="h-8 w-8 min-w-[2rem]" />,
          title: t("solution.vulnerabilityManagement.title"),
          description: t("solution.vulnerabilityManagement.description"),
          url: SolutionLink.VULNERABILITY_MANAGEMENT,
          isCreate: true,
        },
      ] satisfies Omit<ISolution, "enabled">[],
    [t, workspaceId]
  );

  /** Compare hardcoded list with API's solution list. */
  const verifiedSolutionList: ISolution[] = useMemo(() => {
    return defaultSolutions.map((item) => {
      const solution = solutions.find((s) => s.solution.id === item.key);
      return {
        ...item,
        enabled: solution?.enabled || false,
      };
    });
  }, [defaultSolutions, solutions]);

  /** Product list. Calculated by filtering verified solution list & current tab. */
  const filteredProductList: ISolution[] = useMemo(() => {
    if (
      Object.values(ESolutionTabKey)
        .map((item) => item.toString())
        .includes(activeTab)
    ) {
      const productKeys = solutionByTabs[activeTab as ESolutionTabKey].products;

      return verifiedSolutionList
        .filter((item) => productKeys.includes(item.key))
        .sort(
          (a, b) => productKeys.indexOf(a.key) - productKeys.indexOf(b.key)
        );
    }

    return [];
  }, [verifiedSolutionList, activeTab]);

  /** Service list. Calculated by filtering verified solution list & current tab. */
  const filteredServiceList: ISolution[] = useMemo(() => {
    if (
      Object.values(ESolutionTabKey)
        .map((item) => item.toString())
        .includes(activeTab)
    ) {
      const serviceKeys = solutionByTabs[activeTab as ESolutionTabKey].services;

      return verifiedSolutionList
        .filter((item) => serviceKeys.includes(item.key))
        .sort(
          (a, b) => serviceKeys.indexOf(a.key) - serviceKeys.indexOf(b.key)
        );
    }

    return [];
  }, [verifiedSolutionList, activeTab]);

  // --------------- EFFECTS ---------------

  useEffect(() => {
    const fetchSolution = async () => {
      if (!workspaceId) return;
      const response = await listSolutions(workspaceId);
      setSolutions(response);
    };
    fetchSolution();
  }, [workspaceId]);

  // --------------- RENDER ---------------

  return (
    <>
      <div className="pt-5 pb-6 flex flex-col gap-5 min-h-screen v2">
        {/* Header (Breadcrumb + title) */}
        <div className="px-8 flex flex-col gap-4">
          <BreadcrumbV2
            levels={[]}
            currentPage={{
              name: t("breadcrumb.current.allSolutions"),
            }}
          />
          <span className="text-display-xs font-semibold text-gray-v2-900">
            {t("title")}
          </span>
        </div>
        {/* Header (Breadcrumb + title) end */}

        <div className="px-8 flex flex-col gap-5">
          {/* Tab control */}
          <div className="flex gap-3 border-b border-gray-v2-200">
            {tabList.map((tab) => (
              <button
                key={tab.key}
                className={`pb-3 px-1 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? "text-brand-v2-700 border-brand-v2-600"
                    : "text-gray-v2 border-transparent"
                }`}
                onClick={() => {
                  onChangeTab(tab.key);
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {/* Tab control */}

          {/* Solutions */}
          <section className="flex flex-col gap-5">
            {filteredProductList.length > 0 ? (
              <SolutionSectionWrapper title={t("products")}>
                {filteredProductList.map((item) => (
                  <SolutionCard
                    key={item.title}
                    solutionItem={item}
                    role={currentWorkspace?.role}
                  />
                ))}
              </SolutionSectionWrapper>
            ) : null}

            {filteredServiceList.length > 0 ? (
              <SolutionSectionWrapper title={t("services")}>
                {filteredServiceList.map((item) => (
                  <SolutionCard
                    key={item.title}
                    solutionItem={item}
                    role={currentWorkspace?.role}
                  />
                ))}
              </SolutionSectionWrapper>
            ) : null}
          </section>
          {/* Solutions */}
        </div>
      </div>
    </>
  );
};

export default AllSolutionsIndexPage;
